(() => {
  'use strict';
  document.documentElement.classList.add('uc-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  function initHeader() {
    const header = qs('[data-uc-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('uc-scrolled', window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const toggle = qs('[data-uc-menu-toggle]', header);
    const panel = qs('[data-uc-mobile-panel]');
    const close = qs('[data-uc-menu-close]', panel || document);
    const setOpen = (open) => {
      if (!panel || !toggle) return;
      panel.classList.toggle('is-open', open);
      panel.setAttribute('aria-hidden', String(!open));
      toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('uc-menu-open', open);
    };
    toggle?.addEventListener('click', () => setOpen(!panel.classList.contains('is-open')));
    close?.addEventListener('click', () => setOpen(false));
    panel?.addEventListener('click', (event) => {
      if (event.target === panel || event.target.closest('a')) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
  }

  function initReveals() {
    const elements = qsa('[data-uc-reveal]');
    if (!elements.length || reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
    elements.forEach((el) => observer.observe(el));
  }

  function initGlow() {
    if (reduceMotion || window.matchMedia('(pointer: coarse)').matches) return;
    const glow = qs('[data-uc-glow]');
    if (!glow) return;
    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let tx = x;
    let ty = y;
    window.addEventListener('pointermove', (event) => {
      tx = event.clientX;
      ty = event.clientY;
    }, { passive: true });
    const tick = () => {
      x += (tx - x) * 0.12;
      y += (ty - y) * 0.12;
      glow.style.transform = `translate3d(${x - 210}px,${y - 210}px,0)`;
      requestAnimationFrame(tick);
    };
    tick();
  }

  function initHeroScene() {
    const hero = qs('[data-uc-hero]');
    if (!hero || reduceMotion) return;
    const scene = qs('[data-uc-scene]', hero);
    const device = qs('[data-uc-device]', hero);
    const layers = qsa('[data-depth]', hero);
    if (!scene || !device) return;
    let rx = 0;
    let ry = 0;
    let targetX = 0;
    let targetY = 0;
    const dir = document.documentElement.dir === 'rtl' ? 1 : -1;
    scene.addEventListener('pointermove', (event) => {
      const rect = scene.getBoundingClientRect();
      targetX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      targetY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    }, { passive: true });
    scene.addEventListener('pointerleave', () => {
      targetX = 0;
      targetY = 0;
    });
    const tick = () => {
      rx += (targetY - rx) * 0.055;
      ry += (targetX - ry) * 0.055;
      device.style.transform = `rotateY(${dir * 7 + ry * 4}deg) rotateX(${3 - rx * 3}deg) translate3d(${ry * 7}px,${rx * 7}px,10px)`;
      layers.forEach((layer) => {
        const depth = Number(layer.dataset.depth || 18);
        layer.style.marginTransform = '';
        layer.style.translate = `${ry * depth}px ${rx * depth}px`;
      });
      requestAnimationFrame(tick);
    };
    tick();
  }

  function initTiltCards() {
    if (reduceMotion || window.matchMedia('(pointer: coarse)').matches) return;
    qsa('[data-uc-tilt]').forEach((card) => {
      let raf = 0;
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          card.style.transform = `perspective(900px) rotateX(${-y * 7}deg) rotateY(${x * 8}deg) translateY(-5px)`;
        });
      }, { passive: true });
      card.addEventListener('pointerleave', () => {
        card.style.transform = '';
      });
    });
  }

  function initParticles() {
    const canvas = qs('[data-uc-particles]');
    if (!canvas || reduceMotion) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let width = 0;
    let height = 0;
    let particles = [];
    const colors = ['rgba(118,56,232,.32)', 'rgba(228,45,137,.25)', 'rgba(255,138,0,.25)', 'rgba(10,30,66,.16)'];
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(26, Math.min(70, Math.round(width / 24)));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 2.4 + 0.7,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.18,
        color: colors[Math.floor(Math.random() * colors.length)]
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p, index) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
        for (let j = index + 1; j < particles.length; j += 1) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 105) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(109,78,190,${(1 - distance / 105) * 0.07})`;
            ctx.stroke();
          }
        }
      });
      requestAnimationFrame(draw);
    };
    resize();
    draw();
    window.addEventListener('resize', resize, { passive: true });
  }

  function initMagneticButtons() {
    if (reduceMotion || window.matchMedia('(pointer: coarse)').matches) return;
    qsa('[data-uc-magnetic]').forEach((button) => {
      button.addEventListener('pointermove', (event) => {
        const rect = button.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        button.style.transform = `translate3d(${x * 0.08}px,${y * 0.1 - 3}px,0)`;
      });
      button.addEventListener('pointerleave', () => { button.style.transform = ''; });
    });
  }

  function initQuantity() {
    qsa('[data-uc-qty]').forEach((el) => {
      el.addEventListener('change', () => el.form?.submit());
    });
  }


  function initScrollExperience() {
    const progress = qs('[data-uc-scroll-progress]');
    const parallaxItems = qsa('.uc-section-orb, .uc-cta__rocket, .uc-scene__gridlines');
    let ticking = false;
    const update = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const ratio = Math.min(1, Math.max(0, window.scrollY / max));
      if (progress) progress.style.width = `${ratio * 100}%`;
      if (!reduceMotion) {
        parallaxItems.forEach((el, index) => {
          const rect = el.getBoundingClientRect();
          const center = rect.top + rect.height / 2 - window.innerHeight / 2;
          const amount = Math.max(-45, Math.min(45, center * -0.035 * (index % 2 ? 1 : -1)));
          el.style.translate = `0 ${amount}px`;
        });
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initReveals();
    initGlow();
    initHeroScene();
    initTiltCards();
    initParticles();
    initMagneticButtons();
    initQuantity();
    initScrollExperience();
  });
})();
